import type { Catalogs } from '../../../../core/interfaces/backend/api_auth/identity-profile.interface';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, debounceTime, distinctUntilChanged, switchMap, of, catchError } from 'rxjs';
import { ApiAuthService } from '../../../../services/backend/public/api-auth/api-auth.service';
import { IdentityProfileService } from '../../../../services/backend/public/api-auth/identity-profile.service';
import { GovcoConfirmModalComponent } from '../../../../shared/govco/govco-confirm-modal/govco-confirm-modal.component';

@Component({ standalone: true, imports: [ReactiveFormsModule, GovcoConfirmModalComponent],
  templateUrl: './first-access.component.html', styleUrl: './first-access.component.css' })
export class FirstAccessComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(IdentityProfileService);
  private readonly auth = inject(ApiAuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  catalogs: Catalogs = {};
  loading = true;
  saving = false;
  error = '';
  digit = '';
  existingRoles: string[] = [];
  wasComplete = false;
  confirmation: 'save' | 'cancel' | null = null;
  readonly fields = [
    ['numero_identificacion', 'Número de identificación', 10], ['primer_nombre', 'Primer nombre', 20],
    ['segundo_nombre', 'Segundo nombre', 20], ['primer_apellido', 'Primer apellido', 20],
    ['segundo_apellido', 'Segundo apellido', 20], ['correo_electronico', 'Correo electrónico', 50],
    ['telefono', 'Teléfono (7 a 10 dígitos)', 10]
  ] as const;
  readonly legalFields = [ ['nit', 'NIT sin DV', 10], ['razon_social', 'Razón social', 50],
    ['correo_electronico', 'Correo de la entidad', 50], ['telefono', 'Teléfono del representante legal', 10],
    ['representante_legal', 'Representante legal', 20],
    ['numero_identificacion_representante', 'Identificación del representante', 10],
    ['nombre_entidad_territorial', 'Nombre de la entidad territorial', 250],
    ['direccion_entidad_territorial', 'Dirección de la entidad territorial', 250],
    ['nombre_dependencia_responsable', 'Dependencia responsable', 250],
    ['telefono_dependencia_responsable', 'Teléfono de la dependencia', 10],
    ['extension_dependencia_responsable', 'Extensión (opcional)', 10],
    ['correo_dependencia_responsable', 'Correo de la dependencia', 250] ] as const;
  readonly legal = this.fb.group({ nit: [''], razon_social: [''], id_tipo_entidad: [''],
    id_pais_domicilio: [''], id_departamento_domicilio: [''], id_municipio_domicilio: [''],
    correo_electronico: [''], telefono: [''], representante_legal: [''],
    id_tipo_identificacion_representante: [''], numero_identificacion_representante: [''], otro_tipo_entidad: [''], nombre_entidad_territorial: [''],
    direccion_entidad_territorial: [''], nombre_dependencia_responsable: [''], telefono_dependencia_responsable: [''],
    extension_dependencia_responsable: [''], correo_dependencia_responsable: [''] });
  readonly form = this.fb.group({ tipo_persona: ['natural', Validators.required], id_tipo_identificacion: ['', Validators.required],
    numero_identificacion: ['', [Validators.required, Validators.pattern(/^[0-9]{1,10}$/)]],
    primer_nombre: ['', [Validators.required, Validators.maxLength(20)]], segundo_nombre: ['', Validators.maxLength(20)],
    primer_apellido: ['', [Validators.required, Validators.maxLength(20)]], segundo_apellido: ['', Validators.maxLength(20)],
    correo_electronico: ['', [Validators.required, Validators.email, Validators.maxLength(50)]],
    telefono: ['', [Validators.required, Validators.pattern(/^[0-9]{7,10}$/)]],
    id_pais_domicilio: ['', Validators.required], id_departamento_domicilio: [''], id_municipio_domicilio: [''],
    structurer: [false], implementer: [false], persona_juridica: this.legal });

  constructor() {
    this.form.controls.tipo_persona.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.conditions());
    for (const group of [this.form, this.legal]) {
      group.controls.id_pais_domicilio.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        group.patchValue({ id_departamento_domicilio: '', id_municipio_domicilio: '' }, { emitEvent: false }); this.conditions();
      });
      group.controls.id_departamento_domicilio.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        group.controls.id_municipio_domicilio.setValue('', { emitEvent: false });
      });
    }
    this.legal.controls.nit.valueChanges.pipe(debounceTime(300), distinctUntilChanged(),
      switchMap(nit => { this.digit = ''; return /^[0-9]{1,10}$/.test(nit ?? '') ? this.api.digit(nit!).pipe(catchError(() => of(''))) : of(''); }),
      takeUntilDestroyed(this.destroyRef)).subscribe(digit => this.digit = digit);
    this.legal.controls.id_tipo_entidad.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.conditions());
    this.conditions();
  }
  ngOnInit(): void {
    forkJoin({ catalogs: this.api.catalogs(), profile: this.api.profile() }).subscribe({
      next: ({ catalogs, profile }) => {
        this.catalogs = catalogs; this.existingRoles = profile.roles; this.wasComplete = profile.perfil_completo;
        this.form.patchValue(profile as Parameters<typeof this.form.patchValue>[0], { emitEvent: false });
        if (profile.persona_juridica) this.legal.patchValue(profile.persona_juridica, { emitEvent: false });
        this.digit = String(profile.persona_juridica?.['digito_verificacion'] ?? '');
        this.form.patchValue({ structurer: profile.roles.includes('Estructurador'), implementer: profile.roles.includes('Implementador') }, { emitEvent: false });
        this.conditions(); this.loading = false;
      }, error: () => { this.loading = false; this.error = 'No se pudo cargar el perfil. Vuelva a iniciar sesión e inténtelo nuevamente.'; }
    });
  }
  get otherEntity(): boolean { return this.items('tipos_entidad').some(t => t.id_registro === this.legal.controls.id_tipo_entidad.value && t.nombre === 'Otra entidad'); }
  get isLegal(): boolean { return this.form.controls.tipo_persona.value === 'juridica'; }
  items(name: string) { return (this.catalogs[name] ?? []).map(r => r.activo === false ? {...r, nombre: r.nombre + ' (inactivo; valor conservado)'} : r); }
  colombia(country: string | null): boolean { return this.items('paises').some(c => c.id_registro === country && c.codigo_iso2 === 'CO'); }
  departments(country: string | null) { return this.items('departamentos').filter(d => d.id_pais === country); }
  cities(department: string | null) { return this.items('municipios').filter(c => c.id_departamento === department); }
  private conditions(): void {
    for (const [name, control] of Object.entries(this.legal.controls)) {
      const required = this.isLegal && name !== 'extension_dependencia_responsable' && (name !== 'otro_tipo_entidad' || this.otherEntity);
      const validators = required ? [Validators.required] : [];
      if (name === 'otro_tipo_entidad' && !this.otherEntity) control.setValue('', {emitEvent:false});
      if (this.isLegal && ['numero_identificacion_representante','extension_dependencia_responsable'].includes(name)) validators.push(Validators.pattern(/^[0-9]{1,10}$/));
      if (this.isLegal && name === 'nit') validators.push(Validators.pattern(/^[0-9]{1,10}$/));
      if (this.isLegal && ['telefono','telefono_dependencia_responsable'].includes(name)) validators.push(Validators.pattern(/^[0-9]{7,10}$/));
      if (this.isLegal && ['correo_electronico','correo_dependencia_responsable'].includes(name)) validators.push(Validators.email);
      control.setValidators(validators); control.updateValueAndValidity({ emitEvent: false });
    }
    for (const group of [this.form, this.legal]) {
      const required = (group === this.form || this.isLegal) && this.colombia(group.controls.id_pais_domicilio.value);
      for (const key of ['id_departamento_domicilio','id_municipio_domicilio'] as const) {
        group.controls[key].setValidators(required ? [Validators.required] : []);
        if (!required) group.controls[key].setValue('', { emitEvent: false });
        group.controls[key].updateValueAndValidity({ emitEvent: false });
      }
    }
  }
  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || (this.isLegal && !this.digit)) { this.error = 'Complete los campos obligatorios y verifique sus datos.'; return; }
    this.error = ''; this.confirmation = 'save';
  }
  confirm(): void {
    const action = this.confirmation; this.confirmation = null;
    if (action === 'cancel') { void this.router.navigateByUrl(this.wasComplete ? '/dashboard' : '/inicio'); return; }
    if (action !== 'save' || this.saving) return;
    const value = this.form.getRawValue();
    const { structurer, implementer, persona_juridica, ...personal } = value;
    const nullable = (data: Record<string, unknown>) => Object.fromEntries(Object.entries(data).map(([k,v]) => [k, v === '' ? null : v]));
    const payload = { ...nullable(personal), roles: [ ...(structurer ? ['Estructurador'] : []), ...(implementer ? ['Implementador'] : []) ],
      persona_juridica: this.isLegal ? nullable(persona_juridica) : null };
    this.saving = true;
    this.api.save(payload).pipe(switchMap(() => this.auth.restore())).subscribe({
      next: identity => { this.saving = false; void this.router.navigateByUrl(identity.nextRoute ?? '/dashboard'); },
      error: error => { this.saving = false; this.error = error.status === 409 ? 'La identificación ya existe o un rol tiene proyectos vigentes. Revise los datos.' : 'No se pudo guardar. Verifique los datos y vuelva a intentarlo.'; }
    });
  }
}
